import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  StaffSchedule,
  StaffScheduleBulkSaveModel,
  GetStaffScheduleParams,
  GetScheduleForDateParams,
  InsertStaffScheduleParams,
  UpdateStaffScheduleParams
} from '../models/staff-schedule.model';

@Injectable({
  providedIn: 'root'
})
export class StaffScheduleService {
  private API_URL = environment.API_URL + 'StaffSchedule/';

  constructor(private http: HttpClient) { }

  /**
   * Get staff schedules with filters and pagination (Admin only)
   */
  getStaffSchedules(params: GetStaffScheduleParams): Observable<StaffSchedule[]> {
    const queryParams = this.buildQueryString(params);
    return this.http.get<StaffSchedule[]>(this.API_URL + 'GetStaffSchedules' + queryParams)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to load staff schedules')))
      );
  }

  /**
   * Get schedule entries for a specific user
   */
  getScheduleByUser(userId: number, branchId?: number, isActive: boolean = true): Observable<StaffSchedule[]> {
    let url = `${this.API_URL}GetScheduleByUser?userId=${userId}&isActive=${isActive}`;
    if (branchId) {
      url += `&branchId=${branchId}`;
    }
    return this.http.get<StaffSchedule[]>(url)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to load user schedule')))
      );
  }

  /**
   * Get schedules for all staff on a specific date
   */
  getScheduleForDate(params: GetScheduleForDateParams): Observable<StaffSchedule[]> {
    const queryParams = this.buildQueryString(params);
    return this.http.get<StaffSchedule[]>(this.API_URL + 'GetScheduleForDate' + queryParams)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to load schedule for date')))
      );
  }

  /**
   * Create a new schedule entry
   */
  insertSchedule(params: InsertStaffScheduleParams): Observable<{ success: boolean; scheduleID: number }> {
    return this.http.post<{ success: boolean; scheduleID: number }>(this.API_URL + 'InsertSchedule', params)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to create schedule')))
      );
  }

  /**
   * Update an existing schedule entry
   */
  updateSchedule(params: UpdateStaffScheduleParams): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(this.API_URL + 'UpdateSchedule', params)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to update schedule')))
      );
  }

  /**
   * Bulk save weekly schedule for a user
   */
  bulkSaveSchedule(model: StaffScheduleBulkSaveModel): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(this.API_URL + 'BulkSaveSchedule', model)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to save schedule')))
      );
  }

  /**
   * Delete a schedule entry (soft delete)
   * Using POST instead of DELETE to avoid CORS preflight issues
   */
  deleteSchedule(scheduleId: number): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(this.API_URL + 'DeleteSchedule', { scheduleID: scheduleId })
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to delete schedule')))
      );
  }

  /**
   * Helper to build query string from params object
   */
  private buildQueryString(params: any): string {
    const queryParts: string[] = [];
    for (const key in params) {
      if (params[key] !== null && params[key] !== undefined) {
        let value = params[key];
        // Format date values
        if (value instanceof Date) {
          value = value.toISOString().split('T')[0];
        }
        queryParts.push(`${key}=${encodeURIComponent(value)}`);
      }
    }
    return queryParts.length > 0 ? '?' + queryParts.join('&') : '';
  }
}
